import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import TutorCard from "../components/TutorCard";
import { getTutors } from "../lib/storage";

const scoreTutor = (tutor, query) => {
  const haystack = `${tutor.name} ${tutor.subjects.join(" ")} ${tutor.shortBio} ${(tutor.tags || []).join(" ")}`.toLowerCase();
  if (!query) {
    return tutor.rating * 10 + tutor.experienceYears;
  }
  let score = 0;
  if (haystack.includes(query)) score += 50;
  if (tutor.name.toLowerCase().includes(query)) score += 20;
  score += tutor.rating * 5;
  return score;
};

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [tutors, setTutors] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [subject, setSubject] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setTutors(getTutors());
    const sync = () => setTutors(getTutors());
    window.addEventListener("dreameer:data-updated", sync);
    return () => window.removeEventListener("dreameer:data-updated", sync);
  }, []);

  useEffect(() => {
    const query = params.get("search") || "";
    setSearch(query);
  }, [params]);

  const subjectOptions = useMemo(() => {
    const values = Array.from(new Set(tutors.flatMap((tutor) => tutor.subjects))).sort();
    return ["all", ...values];
  }, [tutors]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    let items = tutors.filter((tutor) => {
      const matchesSearch =
        !query ||
        `${tutor.name} ${tutor.subjects.join(" ")} ${tutor.shortBio} ${(tutor.tags || []).join(" ")}`
          .toLowerCase()
          .includes(query);
      const matchesSubject = subject === "all" || tutor.subjects.includes(subject);
      return matchesSearch && matchesSubject;
    });

    items = [...items].sort((a, b) => {
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "experience") return b.experienceYears - a.experienceYears;
      return scoreTutor(b, query) - scoreTutor(a, query);
    });

    return items;
  }, [search, sortBy, subject, tutors]);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, subject]);

  const pageSize = 6;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pagedTutors = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (value.trim()) {
      setParams({ search: value });
    } else {
      setParams({});
    }
  };

  return (
    <section className="section-shell pt-10">
      <div className="surface-panel overflow-hidden p-9 lg:p-12">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
          <SectionHeading
            eyebrow="Tutor Marketplace"
            title="Find Tutors who match the goal, class, and budget"
            copy="Search, filter, and sort verified educators. View profiles, compare pricing, and book a demo instantly through WhatsApp."
          />
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { label: "Verified Tutors", value: `${tutors.length}+` },
              { label: "Popular Route", value: "Boards + Entrance" },
              { label: "Response Time", value: "Within 24 hrs" },
            ].map((item) => (
              <div key={item.label} className="metric-tile">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">{item.label}</p>
                <p className="mt-3 font-display text-3xl font-extrabold text-ink">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-[1.2fr_0.45fr_0.35fr]">
          <label className="space-y-2 text-base font-extrabold text-slate-700">
            <span>Search tutors, subjects, keywords...</span>
            <input
              type="search"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              className="form-field"
              placeholder="Search tutors, subjects, keywords..."
            />
          </label>
          <label className="space-y-2 text-base font-extrabold text-slate-700">
            <span>All Subjects</span>
            <select value={subject} onChange={(event) => setSubject(event.target.value)} className="form-field">
              {subjectOptions.map((item) => (
                <option key={item} value={item}>
                  {item === "all" ? "All Subjects" : item}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-2 text-base font-extrabold text-slate-700">
            <span>Sort Tutors</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="form-field">
              <option value="relevance">Relevance</option>
              <option value="rating">Highest rating</option>
              <option value="experience">Experience</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </label>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-base font-semibold text-slate-600">
          <p>
            Showing {pagedTutors.length} of {filtered.length} tutors
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/#book-demo" className="button-primary">
              Book Demo
            </Link>
            <a href="https://wa.me/919507505612" className="button-secondary">
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10 grid gap-5 xl:grid-cols-3">
        {pagedTutors.map((tutor) => (
          <TutorCard key={tutor.id} tutor={tutor} />
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          className="button-secondary"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        {Array.from({ length: totalPages }).map((_, index) => {
          const nextPage = index + 1;
          return (
            <button
              key={nextPage}
              type="button"
              className={`rounded-full px-4 py-2 text-sm font-extrabold ${
                nextPage === page
                  ? "bg-navy-900 text-white shadow-neon"
                  : "border border-slate-200 bg-white text-slate-600"
              }`}
              onClick={() => setPage(nextPage)}
            >
              {nextPage}
            </button>
          );
        })}
        <button
          type="button"
          className="button-secondary"
          onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          disabled={page === totalPages}
        >
          Next
        </button>
      </div>
    </section>
  );
}
